import { isPlatformBrowser } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  NgZone,
  Output,
  PLATFORM_ID,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { USE_FAKES } from '@freelancer/config';
import { RepetitiveSubscription } from '@freelancer/decorators';
import { UiModule } from '@freelancer/ui';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  InvisibleReCaptchaComponent,
  NgxCaptchaModule,
  ReCaptcha2Component,
} from 'ngx-captcha';
import { Observable, Subscription } from 'rxjs';
import { CAPTCHA_CONFIG } from './captcha.config';
import { CaptchaConfig } from './captcha.interface';

@UntilDestroy({ className: 'CaptchaComponent' })
@Component({
  standalone: true,
  selector: 'fl-captcha',
  imports: [NgxCaptchaModule, UiModule],
  template: `
    @if (!isFake) {
      @if (!isInvisible) {
        @if (isClientSide) {
          <ngx-recaptcha2
            [siteKey]="recaptchaKey"
            [formControl]="control"
            (success)="onSuccess.emit($event)"
          ></ngx-recaptcha2>
        }
      } @else {
        @if (isClientSide) {
          <ngx-invisible-recaptcha
            (ready)="onInvisibleCaptchaReady()"
            [siteKey]="recaptchaKey"
            [formControl]="control"
            (success)="onSuccess.emit($event)"
          ></ngx-invisible-recaptcha>
        }
      }
      <fl-validation-error [control]="control"></fl-validation-error>
    } @else {
      <div class="FakeCaptcha">
        <!-- Testing code: doesn't need to be translated -->
        {{ 'Are you a robot?' }}
        <fl-radio
          [options]="radioOptions"
          [control]="fakeCaptchaControl"
        ></fl-radio>
      </div>
      <fl-validation-error [control]="control"></fl-validation-error>
    }
  `,
  styleUrls: ['./captcha.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CaptchaComponent implements OnInit, OnDestroy {
  config = inject<CaptchaConfig>(CAPTCHA_CONFIG);
  private platformId = inject(PLATFORM_ID);
  private useFakes = inject(USE_FAKES);
  private ngZone = inject(NgZone);

  @Input() control: FormControl<string>;
  @Input() recaptchaKey = this.config.recaptchaPublicKey;
  @Input() isInvisible = false;

  /**
   * Called when captcha receives successful response.
   * Captcha response token is passed to event.
   */
  @Output() onSuccess = new EventEmitter<string>();

  @ViewChild(ReCaptcha2Component) captchaElement: ReCaptcha2Component;
  @ViewChild(InvisibleReCaptchaComponent)
  invisibleCaptchaElement: InvisibleReCaptchaComponent;

  private subscriptions = new Subscription();
  @RepetitiveSubscription()
  private captchaDetectionSubscription?: Subscription;

  isClientSide: boolean;
  isFake: boolean;

  radioOptions = [
    { value: 'PASS', displayText: "I'm not a robot" },
    { value: 'FAIL', displayText: 'I am a robot' },
  ];

  fakeCaptchaControl = new FormControl<'' | 'PASS' | 'FAIL'>('', {
    nonNullable: true,
  });

  ngOnInit(): void {
    this.isFake = this.useFakes;
    this.isClientSide = isPlatformBrowser(this.platformId);
    this.subscriptions.add(
      this.fakeCaptchaControl.valueChanges.subscribe(val => {
        this.control.setValue(
          val === 'PASS' ? '1234' : val === 'FAIL' ? '0000' : '',
        );

        if (val === 'PASS') {
          this.onSuccess.emit('1234');
        }
      }),
    );
  }

  /**
   * Detect when reCAPTCHA challenge is shown and when it's dismissed without completion
   */
  private detectReCaptchaChallenge(): void {
    // Exit early if not running in browser
    if (!this.isClientSide) {
      return;
    }

    // We need to observe the document body because reCAPTCHA creates
    // overlay elements at the document level, not within our component
    const targetElement = document.body;

    const observerConfig = {
      childList: true,
      attributes: false,
      attributeOldValue: false,
      characterData: false,
      characterDataOldValue: false,
      subtree: false,
    };

    const detectChallengePresence = (): Observable<Element> => {
      return new Observable<Element>(observer => {
        const captchaObserver = new MutationObserver(mutations => {
          mutations.forEach(mutation => {
            if (mutation.addedNodes.length) {
              const reCaptchaParentContainer = mutation
                .addedNodes[0] as Element;
              const reCaptchaIframe = reCaptchaParentContainer.querySelectorAll(
                'iframe[title*="recaptcha"]',
              );

              if (reCaptchaIframe.length) {
                captchaObserver.disconnect();
                observer.next(reCaptchaParentContainer);
                observer.complete();
              }
            }
          });
        });

        captchaObserver.observe(targetElement, observerConfig);

        // Return teardown function that will be called when unsubscribed
        return () => {
          captchaObserver.disconnect();
        };
      });
    };

    this.captchaDetectionSubscription = detectChallengePresence()
      .pipe(untilDestroyed(this))
      .subscribe(challengeOverlayDiv => {
        // Create closure observer locally instead of as a class property
        const closureObserver = new MutationObserver(() => {
          const div = challengeOverlayDiv as HTMLElement;
          // Get reCAPTCHA response from the appropriate component
          const hasResponse = this.isInvisible
            ? this.invisibleCaptchaElement?.getResponse()
            : this.captchaElement?.getResponse();

          if (div.style.visibility === 'hidden' && !hasResponse) {
            // Challenge was dismissed without completion, reset the captcha
            closureObserver.disconnect();
            this.ngZone.run(() => {
              this.reset();
            });
          }
        });

        closureObserver.observe(challengeOverlayDiv, {
          attributes: true,
          attributeFilter: ['style'],
        });
      });

    // Add to the master subscription for cleanup on destroy
    this.subscriptions.add(this.captchaDetectionSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onInvisibleCaptchaReady(): void {
    this.invisibleCaptchaElement.execute();

    if (this.isClientSide && !this.isFake) {
      this.detectReCaptchaChallenge();
    }
  }

  reset(): void {
    // Avoid test failure when using fake
    if (this.captchaElement) {
      this.captchaElement.resetCaptcha();
    } else if (this.invisibleCaptchaElement) {
      this.invisibleCaptchaElement.resetCaptcha();
      this.invisibleCaptchaElement.execute();
      // Re-setup challenge detection after reset for invisible captcha
      if (this.isClientSide && !this.isFake) {
        this.detectReCaptchaChallenge();
      }
    } else {
      this.fakeCaptchaControl.reset();
    }
  }
}
